<template>
  <v-layout row wrap fill-height align-center justify-center class="secondary lighten-1">
    <v-flex xs12 sm4 align-center justify-center>
      <v-card class="secondary lighten-1 elevation-0">
        <v-card-title primary-title>
          <div class="white--text">
            <h3 class="headline mb-0">{{ messages.label.SIGN_IN }}</h3>
            <div v-html="messages.info.SIGN_IN_MPX"></div>
          </div>
        </v-card-title>
        <v-card-text>
          <v-form ref="form">
            <v-text-field :label="messages.label.USER_NAME" v-model="userName" box background-color="indigo lighten-5"
              class="ttu" placeholder="Enter username" required></v-text-field>
            <v-text-field :label="messages.label.PASSWORD" v-model="password" placeholder="Enter password" box
              background-color="indigo lighten-5" class="ttu" type="password" @keyup.enter="signIn" required>
            </v-text-field>
          </v-form>
          <v-alert color="error" icon="error" value="true" v-if="loginErrorMsg !== ''">
            <span v-html="loginErrorMsg"></span>
          </v-alert>
          <v-alert color="warning" icon="warning" value="true" v-else-if="isTokenInvalid">
            <span>{{ messages.error.SESSION_EXPIRED }}</span>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" :loading="signInInProgress" @click="signIn">{{
              messages.label.SIGN_IN
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapState, mapMutations } from "vuex"
import AppService from "@/AppService"
import * as appDataService from "@/AppDataService"
import * as authDataService from "@/Auth/AuthDataService"
import * as utils from "@/Shared/utils"
import messages from "@/Auth/messages.json"
import {
  SET_MPX_USER_INFO,
  SET_MVATS_AUTHORIZE_INFO,
  SET_OPSUI_AUTHORIZE_INFO,
  SET_MPX_PARENT_ACCOUNT
} from "@/store/constants/mutation-types"

export default {
  name: "MPXLogin",
  data() {
    return {
      userName: "",
      password: "",
      signInInProgress: false,
      loginErrorMsg: "",
      isTokenInvalid: false
    }
  },
  computed: {
    ...mapState(["mpxParentAccount", "opsUIParentAccount"])
  },
  methods: {
    ...mapMutations([
      SET_MPX_USER_INFO,
      SET_MVATS_AUTHORIZE_INFO,
      SET_OPSUI_AUTHORIZE_INFO,
      SET_MPX_PARENT_ACCOUNT
    ]),

    /**
     * signIn - authenticate user with mpx credentials
     */
    signIn() {
      let signInResponse = {}
      let mvatsAuthorizeResponse = {}
      let opsUIAuthorizeResponse = {}
      let token = ""
      let hasOpsUIAccess = false
      let hasMVATSAccess = false
      this.signInInProgress = true

      authDataService
        .mpxSignIn(this.userName, this.password)
        .then(response => {
          this.loginErrorMsg = ""
          signInResponse = response
          token = signInResponse.token
          this.SET_MPX_USER_INFO(signInResponse) // Update store
          // Persist to local storage
          return appDataService
            .setMPXUserInfo(signInResponse)
            .then(mpxUserInfo => {
              return mpxUserInfo
            })
            .catch(error => {
              return Promise.reject(error)
            })
        })
        .then(() => {
          // Retreive authorize information for MVATS
          return authDataService
            .mpxAuthorize(token, AppService.getMvatsServiceString())
            .then(response => {
              hasMVATSAccess = true
              mvatsAuthorizeResponse = response
              this.SET_MVATS_AUTHORIZE_INFO(mvatsAuthorizeResponse) // Update store
              // Persist to local storage
              return appDataService
                .setMVATSAuthorizeInfo(mvatsAuthorizeResponse)
                .then(mvatsAuthorizeResponse => {
                  return mvatsAuthorizeResponse
                })
                .catch(error => {
                  return Promise.reject(error)
                })
            })
            .catch(error => {
              return Promise.reject(error)
            })
        })
        .then(() => {
          // Retrieve authorize information for Operations UI
          return authDataService
            .mpxAuthorize(token, AppService.getOpsUIServiceString())
            .then(response => {
              hasOpsUIAccess = true
              opsUIAuthorizeResponse = response
              this.SET_OPSUI_AUTHORIZE_INFO(opsUIAuthorizeResponse) // Update store
              // Persist to local storage
              return appDataService
                .setOpsUIAuthorizeInfo(opsUIAuthorizeResponse)
                .then(opsUIAuthorizeResponse => {
                  return opsUIAuthorizeResponse
                })
                .catch(error => {
                  return Promise.reject(error)
                })
            })
            .catch(error => {
              return Promise.reject(error)
            })
        })
        .then(() => {
          this.navigateToMPX()
        })
        .catch(error => {
          console.log(error)
          let isServerError = utils.isMPXError(error)
          if (isServerError) {
            let responseCode = error.response.data.responseCode
            if (responseCode === 403) {
              if (!hasMVATSAccess && !hasOpsUIAccess) {
                this.loginErrorMsg =
                  "<b>" + this.userName + "</b>" + messages.error.NO_PERMISSION
                this.clearUserData()
              } else {
                // Let the user in if they have access to either MVATS / Ops UI
                this.navigateToMPX()
              }
            } else if (responseCode === 401) {
              this.loginErrorMsg =
                messages.error.LOGIN_ERROR + "<b>" + this.userName + "</b>"
              this.clearUserData()
            }
          } else {
            // Default message to display
            this.loginErrorMsg =
              messages.error.LOGIN_ERROR + "<b>" + this.userName + "</b>"
          }
          // Ensure user data is cleared before publishing event
        })
        .finally(() => {
          this.isTokenInvalid = false
          this.signInInProgress = false
        })
    },

    /**
     * Clears out user data from local storage and vuex state
     */
    clearUserData() {
      appDataService
        .setMPXUserInfo({})
        .then(() => {
          this.SET_MPX_USER_INFO({})
          return appDataService.setMVATSAuthorizeInfo({})
        })
        .then(() => {
          this.SET_MVATS_AUTHORIZE_INFO({})
          return appDataService.setOpsUIAuthorizeInfo({})
        })
        .then(() => {
          this.SET_OPSUI_AUTHORIZE_INFO({})
          return appDataService.setMPXParentAccountInfo({})
        })
        .then(() => {
          this.SET_MPX_PARENT_ACCOUNT({})
          AppService.$emit("event:mpxLoginFailure")
        })
    },

    /**
     * navigateToMPX -
     */
    navigateToMPX() {
      let redirectTo = "/home" // Default re-direction

      if (!this.$lodash.isUndefined(this.$route.query.redirect)) {
        redirectTo = this.$route.query.redirect
        this.$router.push({
          name: "MPXAccountSelection",
          query: {
            redirect: redirectTo
          }
        })
      } else {
        this.$router.push({
          path: redirectTo
        })
      }
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (to.query && to.query.token === "false") {
        vm.isTokenInvalid = true
      }
    })
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
  }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/Auth/MPXLogin.vue